import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  Announcement,
  AnnouncementsContext,
  AnnouncementsContextValue,
} from '../contexts/AnnouncementsContext';

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const ANNOUNCEMENTS_KEY = 'cheereval_demo_announcements';
const DISMISSED_KEY = 'cheereval_dismissed_demo';

interface StoredAnnouncement {
  id: string;
  text: string;
  createdAt: string; // ISO string
  createdByName: string;
}

function loadAnnouncements(): Announcement[] {
  try {
    const raw = localStorage.getItem(ANNOUNCEMENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as unknown[])
      .filter((x): x is StoredAnnouncement =>
        typeof x === 'object' && x !== null &&
        typeof (x as StoredAnnouncement).id === 'string' &&
        typeof (x as StoredAnnouncement).text === 'string' &&
        typeof (x as StoredAnnouncement).createdAt === 'string' &&
        typeof (x as StoredAnnouncement).createdByName === 'string',
      )
      .map((x) => ({ ...x, createdAt: new Date(x.createdAt) }));
  } catch {
    return [];
  }
}

function saveAnnouncements(announcements: readonly Announcement[]): void {
  const stored: StoredAnnouncement[] = announcements.map((a) => ({
    ...a,
    createdAt: a.createdAt.toISOString(),
  }));
  localStorage.setItem(ANNOUNCEMENTS_KEY, JSON.stringify(stored));
}

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set((parsed as unknown[]).filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function saveDismissed(dismissed: ReadonlySet<string>): void {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(dismissed)));
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

let demoIdCounter = 0;

export function DemoAnnouncementsProvider({ children }: { children: React.ReactNode }) {
  const [announcements, setAnnouncements] = useState<readonly Announcement[]>(() => loadAnnouncements());
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => loadDismissed());

  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(async () => {
    setAnnouncements([]);
    localStorage.removeItem(ANNOUNCEMENTS_KEY);
    setDismissed(new Set());
    localStorage.removeItem(DISMISSED_KEY);
  }, []);

  const create = useCallback(async (text: string, createdByName: string) => {
    demoIdCounter += 1;
    // Timestamp-based ID avoids counter-reset collisions with persisted dismissed set.
    const id = `demo-${Date.now()}-${demoIdCounter}`;
    const newItem: Announcement = { id, text, createdAt: new Date(), createdByName };
    setAnnouncements((prev) => {
      const next = [newItem, ...prev];
      saveAnnouncements(next);
      return next;
    });
    window.setTimeout(() => {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(id);
        saveDismissed(next);
        return next;
      });
    }, 3000);
  }, []);

  const undismissed = useMemo(
    () => announcements.filter((a) => !dismissed.has(a.id)),
    [announcements, dismissed],
  );

  const value = useMemo<AnnouncementsContextValue>(
    () => ({ announcements, undismissed, isLoading: false, dismiss, create, clearAll }),
    [announcements, undismissed, dismiss, create, clearAll],
  );

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
}
